import React from 'react';
import GridItem from '../../../layout/GridComponents/GridItem';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import type { VarApproach } from '../../../../api/mongodb/preferences.types';
import type { ValueAtRiskPayload } from '../../../../api/rs-hg/risk/valueAtRisk.types';

export interface VarTableData {
  number: number;
  portfolio: string;
  equity: number;
  commodity: number;
  forex: number;
  fixedIncome: number;
  cash: number;
  alternative: number;
  total: number;
}
export interface VarComponentProps {
  pageData: ValueAtRiskPayload;
  fundName: string;
}

export type RiskMetric = 'var' | 'cvar' | 'dvar';
export type RiskOutput = 'Cash' | VarApproach;
export interface VarWrapperProps {
  riskOutput: RiskOutput | null;
  riskMetric: RiskMetric | null;
  renderData: VarTableData[];
}

interface GenerateVarTableProps {
  varData: ValueAtRiskPayload['var_data'];
  nav: ValueAtRiskPayload['nav'];
  portfolioName: ValueAtRiskPayload['portfolio_name'];
  riskOutput: RiskOutput | null;
  riskMetric: RiskMetric | null;
}

/**
 * The risk output adjusts which data from the json we choose.
 * If the risk output is absolute or relative we provide the value, divided by nav
 * if Cash - it should just be the value
 */
function generateVarValueBasedOnRiskOutput(
  riskOutput: RiskOutput,
  value: number,
  nav: number,
  benchmarkRisk: number,
) {
  try {
    switch (riskOutput) {
      case 'Cash':
        return value;

      case 'relative':
        return value / benchmarkRisk;

      case 'absolute':
        return value / nav;

      default:
        throw new Error('Unknown risk output: ' + riskOutput);
    }
  } catch (err) {
    console.error('[Error]', err, { fallback: value / nav });
    return value / nav;
  }
}

function generateTableData({
  varData,
  nav,
  portfolioName,
  riskOutput,
  riskMetric,
}: GenerateVarTableProps): VarTableData[] {
  if (!varData || !portfolioName || !riskOutput || !riskMetric) {
    console.warn('[VAR] Missing required inputs in generateTableData', {
      hasVarData: !!varData,
      hasPortfolioName: !!portfolioName,
      riskOutput,
      riskMetric,
    });
    return [];
  }

  const data = varData;
  // if the risk metric is var we need to use risk data
  const keyToUse = riskMetric === 'var' ? 'risk data' : riskMetric;
  // the object containing the data, cvar, dvar etc.
  const dataObjectByKey = data[keyToUse];
  // if for some reason the key does not exist or isn't an object, bail out gracefully
  if (!dataObjectByKey || typeof dataObjectByKey !== 'object') {
    return [];
  }

  const benchmarkRisk = data['benchmark_risk'];

  // Portfolio keys are numeric strings ("1".."7"); skip metadata keys like "diagnostic"
  return Object.keys(dataObjectByKey)
    .filter((key) => !Number.isNaN(Number(key)))
    .map((key: string) => ({
      number: Number(key),
      portfolio: portfolioName[key],
      equity: generateVarValueBasedOnRiskOutput(
        riskOutput,
        dataObjectByKey[key]['1'],
        nav,
        benchmarkRisk,
      ),
      commodity: generateVarValueBasedOnRiskOutput(
        riskOutput,
        dataObjectByKey[key]['2'],
        nav,
        benchmarkRisk,
      ),
      forex: generateVarValueBasedOnRiskOutput(
        riskOutput,
        dataObjectByKey[key]['3'],
        nav,
        benchmarkRisk,
      ),
      fixedIncome: generateVarValueBasedOnRiskOutput(
        riskOutput,
        dataObjectByKey[key]['4'],
        nav,
        benchmarkRisk,
      ),
      cash: generateVarValueBasedOnRiskOutput(
        riskOutput,
        dataObjectByKey[key]['5'],
        nav,
        benchmarkRisk,
      ),
      alternative: generateVarValueBasedOnRiskOutput(
        riskOutput,
        dataObjectByKey[key]['6'],
        nav,
        benchmarkRisk,
      ),
      total: generateVarValueBasedOnRiskOutput(
        riskOutput,
        dataObjectByKey[key]['Total'],
        nav,
        benchmarkRisk,
      ),
      // occasionally the server returns NaN so we need to do a check for that)
    }))
    .filter((datum) => !isNaN(datum.number));
}

function varComponentWrapper(
  Component: React.ComponentType<VarComponentProps & VarWrapperProps>,
) {
  const WrappedComponent: React.FC<VarComponentProps> = (props) => {
    const riskOutput = useTitleBarSelect('riskOutput') as RiskOutput;
    const riskMetric = useTitleBarSelect('riskMetric') as RiskMetric;

    const data = generateTableData({
      varData: props.pageData.var_data,
      nav: props.pageData.nav,
      portfolioName: props.pageData.portfolio_name,
      riskOutput,
      riskMetric,
    });

    return (
      <GridItem
        xs={12}
        xl={6}
        card
        cardStyle={{ height: '100%' }}
        style={{ alignSelf: 'stretch' }}
      >
        <Component
          {...props}
          riskOutput={riskOutput}
          riskMetric={riskMetric}
          renderData={data}
        />
      </GridItem>
    );
  };

  return WrappedComponent;
}

export default varComponentWrapper;
